import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useId } from 'react';
import { cx } from '@/lib/cx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  ref?: Ref<HTMLInputElement>;
  label: ReactNode;
  /** Secondary line under the label, for explaining a consequence. */
  description?: ReactNode;
}

/**
 * A real `<input type="checkbox">` with a label, rather than a styled div.
 *
 * The whole row is the label, so the hit area is the text as well as the box —
 * a 16px target alone fails the 44px rule on a phone.
 */
export function Checkbox({ label, description, className, id, ...rest }: CheckboxProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const descriptionId = description ? `${inputId}-description` : undefined;

  return (
    <div className={cx('flex gap-2.5', className)}>
      <input
        id={inputId}
        type="checkbox"
        aria-describedby={descriptionId}
        className="peer mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-xs border-border accent-accent-brand disabled:cursor-not-allowed disabled:opacity-50"
        {...rest}
      />
      <div className="min-w-0 peer-disabled:opacity-50 peer-disabled:[&_label]:cursor-not-allowed">
        <label htmlFor={inputId} className="cursor-pointer text-sm text-fg-secondary select-none">
          {label}
        </label>
        {description && (
          <p id={descriptionId} className="mt-0.5 text-xs text-fg-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
